'use client';

import React from 'react';
import { Truck } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { POPULAR_ICONS } from '../blockHelpers';
import { BlockProps } from './types';
import { useLocalizeHref } from '@/core/i18n/navigation';
import { ResponsiveImage } from '../shared/ResponsiveImage';

export interface FeatureCard {
  iconName?: string;
  image?: string;
  hoverImage?: string;
  /** Small uppercase line above the title ("INTERAKTYWNE HOLOGRAMY 3D"). */
  eyebrow?: string;
  title: string;
  description: string;
  href?: string;
}

const DEMO_CARDS: FeatureCard[] = [
  { iconName: 'Truck', title: 'Szybka wysyłka', description: 'Zamówienia realizujemy w 1-2 dni robocze.' },
  { iconName: 'Shield', title: 'Bezpieczne zakupy', description: 'Płatności obsługiwane przez zaufanych operatorów.' },
  { iconName: 'RefreshCw', title: 'Łatwe zwroty', description: '14 dni na zwrot bez podawania przyczyny.' },
];

const ASPECT_CLASS: Record<string, string> = {
  square: 'aspect-square',
  '4:3': 'aspect-[4/3]',
  '16:9': 'aspect-[16/9]',
  '3:4': 'aspect-[3/4]',
};

export function FeatureCardsBlock({ block, currentStyles, isAdmin, viewport }: BlockProps) {
  const localizeHref = useLocalizeHref();
  const settings = block.settings || {};
  const cards: FeatureCard[] = settings.cards || [];
  const columns = settings.columns || 3;
  const columnsMobile = settings.columnsMobile || 1;
  const gapVal = currentStyles.gap !== undefined ? currentStyles.gap : settings.gap;
  const gap = gapVal !== undefined ? `${gapVal}px` : '24px';

  // Admin canvas simulates viewport by scaling a real-width div — Tailwind's md:/sm:/lg:
  // classes below key off the real browser window, not that scaled width, so they never
  // react to the viewport toggle here. Mirror the storefront's own breakpoints in JS instead
  // (sm:640/md:768/lg:1024, matching the canvas's tablet=768/mobile=375 simulated widths);
  // at tablet (768px) md: is already active but lg: isn't, so the 4-column preset (sm:2/lg:4) shows 2.
  const adminColumns = viewport === 'mobile' ? columnsMobile : viewport === 'tablet' ? (columns === 4 ? 2 : columns) : columns;

  // 'icon': a small icon/picture above the text (default). 'cover': the picture spans the card's
  // full width on top, like a category or product tile.
  const cover = settings.imageLayout === 'cover';
  const centered = settings.align === 'center';
  const imageHover: 'none' | 'zoom' | 'dim' = settings.imageHover || 'none';
  const cardBg = currentStyles.cardBg || '#ffffff';
  const cardBorderColor = currentStyles.cardBorderColor || '#e2e8f0';
  const cardBorderWidth = Number(currentStyles.cardBorderWidth ?? 1);
  const cardBorderRadius = currentStyles.cardBorderRadius !== undefined ? `${currentStyles.cardBorderRadius}px` : '16px';
  const iconColor = currentStyles.iconColor || '#2563eb';
  const titleColor = currentStyles.titleColor || '#0f172a';
  const descColor = currentStyles.descColor || '#64748b';
  const eyebrowColor = currentStyles.eyebrowColor || descColor;
  const cardPadding = currentStyles.cardPadding !== undefined ? `${currentStyles.cardPadding}px` : undefined;
  const iconSize = Number(currentStyles.iconSize ?? (cards.some((card) => card.image) ? 48 : 40));
  const px = (value: unknown) => (value !== undefined && value !== null && value !== '' ? `${value}px` : undefined);
  const titleFontSize = px(currentStyles.titleFontSize);
  const descFontSize = px(currentStyles.descFontSize);
  const eyebrowFontSize = px(currentStyles.eyebrowFontSize);

  const displayCards = cards.length > 0 ? cards : (isAdmin ? DEMO_CARDS : []);
  if (displayCards.length === 0 && !isAdmin) return null;

  const blockClass = `feature-cards-${block.id}`;
  const imageSizes = `(max-width: 767px) ${Math.round(100 / columnsMobile)}vw, ${Math.round(100 / columns)}vw`;

  return (
    <div className={cn('w-full', blockClass)}>
      <style>{`
        .${blockClass} .feature-card {
          background: ${cardBg};
          border: ${cardBorderWidth}px solid ${cardBorderWidth > 0 ? cardBorderColor : 'transparent'};
          border-radius: ${cardBorderRadius};
        }
        .${blockClass} .feature-card-title { color: ${titleColor}; ${titleFontSize ? `font-size: ${titleFontSize};` : ''} }
        .${blockClass} .feature-card-desc { color: ${descColor}; ${descFontSize ? `font-size: ${descFontSize};` : ''} }
        .${blockClass} .feature-card-eyebrow { color: ${eyebrowColor}; ${eyebrowFontSize ? `font-size: ${eyebrowFontSize};` : ''} }
        .${blockClass} .feature-card-icon { color: ${iconColor}; }
      `}</style>

      {isAdmin && cards.length === 0 && (
        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-600 font-semibold">
          Podgląd demo — dodaj karty w panelu bocznym
        </div>
      )}

      <div
        className={cn(
          'grid',
          !isAdmin && cn(
            columnsMobile === 2 ? 'grid-cols-2' : 'grid-cols-1',
            columns === 1 ? 'md:grid-cols-1' :
            columns === 2 ? 'md:grid-cols-2' :
            columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' :
            'md:grid-cols-3'
          )
        )}
        style={{ gap, ...(isAdmin ? { gridTemplateColumns: `repeat(${adminColumns}, 1fr)` } : {}) }}
      >
        {displayCards.map((card, i) => {
          const IconComponent = card.iconName ? (POPULAR_ICONS[card.iconName] || Truck) : null;
          const hoverClass = imageHover === 'zoom'
            ? 'transition-transform duration-500 group-hover/card:scale-105'
            : imageHover === 'dim' ? 'transition-opacity duration-300 group-hover/card:opacity-80' : '';

          const picture = card.image ? (
            cover ? (
              <div className={cn('relative w-full overflow-hidden', ASPECT_CLASS[settings.imageAspect] || 'aspect-square')}>
                <ResponsiveImage src={card.image} alt="" sizes={imageSizes} className={cn('absolute inset-0 w-full h-full object-cover', hoverClass, card.hoverImage && 'group-hover/card:opacity-0')} />
                {card.hoverImage && (
                  <ResponsiveImage src={card.hoverImage} alt="" sizes={imageSizes} className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover/card:opacity-100 transition-opacity" />
                )}
              </div>
            ) : (
              <div className="relative shrink-0" style={{ width: iconSize, height: iconSize }}>
                <ResponsiveImage src={card.image} alt="" sizes={`${iconSize * 2}px`} className={cn('absolute inset-0 w-full h-full object-contain', hoverClass, card.hoverImage && 'group-hover/card:opacity-0 transition-opacity')} />
                {card.hoverImage && (
                  <ResponsiveImage src={card.hoverImage} alt="" sizes={`${iconSize * 2}px`} className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover/card:opacity-100 transition-opacity" />
                )}
              </div>
            )
          ) : IconComponent ? (
            <div className="feature-card-icon shrink-0" style={{ width: iconSize, height: iconSize }}>
              <IconComponent className="w-full h-full" strokeWidth={1.75} />
            </div>
          ) : null;

          const text = (
            <>
              {card.eyebrow && <div className="feature-card-eyebrow text-[11px] font-medium uppercase tracking-[0.2em]">{card.eyebrow}</div>}
              <h3 className="feature-card-title font-bold text-base">{card.title}</h3>
              {card.description && <div className="feature-card-desc text-sm leading-relaxed whitespace-pre-line">{card.description}</div>}
            </>
          );

          const content = (
            <div
              className={cn(
                'feature-card group/card h-full flex flex-col overflow-hidden transition-shadow',
                centered ? 'items-center text-center' : 'items-start',
                !cover && 'gap-3',
                !cover && !cardPadding && 'p-6',
              )}
              style={!cover && cardPadding ? { padding: cardPadding } : undefined}
            >
              {cover ? (
                <>
                  {picture}
                  <div
                    className={cn('w-full flex flex-col gap-2', centered ? 'items-center' : 'items-start', !cardPadding && 'p-5')}
                    style={cardPadding ? { padding: cardPadding } : undefined}
                  >
                    {text}
                  </div>
                </>
              ) : (
                <>
                  {picture}
                  {text}
                </>
              )}
            </div>
          );

          if (card.href && !isAdmin) {
            return (
              <a key={i} href={localizeHref(card.href)} className="block h-full">
                {content}
              </a>
            );
          }
          return <React.Fragment key={i}>{content}</React.Fragment>;
        })}
      </div>
    </div>
  );
}
